import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Calendar as CalendarIcon, List } from 'lucide-react'
import { appointmentsApi, Appointment } from '../../lib/api/appointments'
import { SchedulingWizard } from '../../components/appointments/SchedulingWizard'
import { CalendarGrid } from '../../components/calendar/CalendarGrid'
import { TimeSlotModal } from '../../components/calendar/TimeSlotModal'

export const Route = createFileRoute('/dashboard/calendar')({
  component: CalendarComponent,
})

interface TimeSlot {
  time: string
  hour: number
  minute: number
  appointments: Appointment[]
}

function CalendarComponent() {
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>('weekly')
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null)
  const [slotModalDate, setSlotModalDate] = useState('')
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false)

  // Get current user info
  const { data: currentUserData } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await fetch('/api/v1/current_user', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      return response.json()
    }
  })

  const currentUser = currentUserData?.user
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner'
  
  // Get team member ID if user is a team member
  const { data: teamMemberData } = useQuery({
    queryKey: ['my-team-member'],
    queryFn: async () => {
      if (isAdmin) return null
      const response = await fetch('/api/v1/team_members', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await response.json()
      return data.team_members?.find((tm: any) => tm.user.id === currentUser?.id)
    },
    enabled: !!currentUser && !isAdmin
  })

  // Get date range based on view mode
  const getDateRange = () => {
    if (viewMode === 'daily') {
      const dateStr = selectedDate.toISOString().split('T')[0]
      return { startDate: dateStr, endDate: dateStr }
    }
    
    // Weekly view
    const date = new Date(selectedDate)
    const day = date.getDay()
    const diff = date.getDate() - day + (day === 0 ? -6 : 1)
    const monday = new Date(date.setDate(diff))
    
    const startDate = monday.toISOString().split('T')[0]
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    const endDate = sunday.toISOString().split('T')[0]
    
    return { startDate, endDate }
  }

  const { startDate, endDate } = getDateRange()

  // Fetch appointments (admin sees all, team members see only theirs)
  const { data, isLoading } = useQuery({
    queryKey: ['appointments', startDate, endDate, isAdmin, teamMemberData?.id],
    queryFn: () => appointmentsApi.getAll({ 
      start_date: startDate, 
      end_date: endDate,
      ...(!isAdmin && teamMemberData?.id && { team_member_id: teamMemberData.id })
    }),
    enabled: isAdmin || !!teamMemberData
  })

  const appointments = data?.appointments || []

  const getDateRangeLabel = () => {
    if (viewMode === 'daily') {
      return selectedDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long', 
        day: 'numeric' 
      })
    }
    const { startDate, endDate } = getDateRange()
    const start = new Date(startDate)
    const end = new Date(endDate)
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
  }

  const navigate = (direction: 'prev' | 'next') => {
    const date = new Date(selectedDate)
    const days = viewMode === 'daily' ? 1 : 7
    date.setDate(date.getDate() + (direction === 'next' ? days : -days))
    setSelectedDate(date)
  }

  const goToToday = () => {
    setSelectedDate(new Date())
  }

  const handleTimeSlotClick = (slot: TimeSlot, date: string) => {
    setSelectedSlot(slot)
    setSlotModalDate(date)
    setIsSlotModalOpen(true)
  }

  const handleCloseSlotModal = () => {
    setIsSlotModalOpen(false)
    setSelectedSlot(null)
    setSlotModalDate('')
  }

  const handleEmptySlotClick = (date: string, time: string) => {
    setSelectedDate(new Date(date))
    setIsSchedulingOpen(true)
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <div>
            <h1 className="text-3xl font-bold">Calendar</h1>
            <p className="text-base-content/70 mt-1">
              View and manage appointments
            </p>
          </div>
          <button className="btn btn-accent text-white" onClick={() => setIsSchedulingOpen(true)}>
            + New Appointment
          </button>
        </div>

        {/* Calendar Navigation */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
              <div className="flex gap-2">
                <button className="btn btn-sm btn-ghost" onClick={() => navigate('prev')}>←</button>
                <button className="btn btn-sm btn-accent text-white" onClick={goToToday}>Today</button>
                <button className="btn btn-sm btn-ghost" onClick={() => navigate('next')}>→</button>
              </div>
              <h3 className="font-bold text-lg">
                {getDateRangeLabel()}
              </h3>
              <div className="btn-group">
                <button 
                  className={`btn btn-sm ${viewMode === 'daily' ? 'btn-accent text-white' : 'btn-ghost'}`}
                  onClick={() => setViewMode('daily')}
                >
                  <List className="w-4 h-4 mr-1" />
                  Daily
                </button>
                <button 
                  className={`btn btn-sm ${viewMode === 'weekly' ? 'btn-accent text-white' : 'btn-ghost'}`}
                  onClick={() => setViewMode('weekly')}
                >
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Weekly
                </button>
              </div>
            </div>

            {/* Calendar Grid View */}
            {isLoading ? (
              <div className="text-center py-8">Loading appointments...</div>
            ) : (
              <CalendarGrid
                appointments={appointments}
                viewMode={viewMode}
                selectedDate={selectedDate}
                onTimeSlotClick={handleTimeSlotClick}
                onEmptySlotClick={handleEmptySlotClick}
              />
            )}
          </div>
        </div>
      </div>

      {/* Scheduling Wizard */}
      <SchedulingWizard
        isOpen={isSchedulingOpen}
        onClose={() => setIsSchedulingOpen(false)}
        selectedDate={selectedDate.toISOString().split('T')[0]}
      />

      {/* Time Slot Detail Modal */}
      <TimeSlotModal
        isOpen={isSlotModalOpen}
        onClose={handleCloseSlotModal}
        slot={selectedSlot}
        date={slotModalDate}
        allAppointments={appointments}
      />
    </>
  )
}
